<script setup lang="ts">
import { storeToRefs } from "pinia";
import { BREADCRUMBS } from "~/core/constants/breadcrumbs";
import { canManageThirdPartyApps } from "~/modules/applicationManagement/utils/canManageThirdPartyApps";
import { useFetchActiveLicenseInformation } from "~/modules/license/composables/useFetchActiveLicenseInformation";
import { LicenseFeatureLimitName } from "~/modules/license/models/LicenseFeatureLimitName";
import { LicenseFeatureName } from "~/modules/license/models/LicenseFeatureName.enum";
import { useLicenseStore } from "~/modules/license/stores/useLicenseStore";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcPlus from "~icons/lxc/plus";

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const licenseStore = useLicenseStore();
const { fetchActiveLicenseInformation } = useFetchActiveLicenseInformation({
  organizationCode: currentOrganization.value?.code,
});

const createThirdPartyApplication = ref(false);

const newInterfaceDisabled = computed(() => {
  return !canManageThirdPartyApps();
});

const onNewInterfaceButtonClick = () => {
  createThirdPartyApplication.value = true;
};

const isThirdPartyAppLimitReached = computed(() => {
  const licenseFeatureThirdPartyApp =
    licenseStore.activeLicenseInformation?.features?.find(
      (feature) => feature.name === LicenseFeatureName.INTERFACES_THIRD_PARTY,
    );
  const licenseFeatureThirdPartyAppLimit =
    licenseFeatureThirdPartyApp?.limits?.find(
      (limit) => limit.name === LicenseFeatureLimitName.MAX_INTERFACES,
    );
  const licenseFeatureThirdPartyAppTotal = licenseFeatureThirdPartyApp?.total;

  return (
    licenseFeatureThirdPartyAppLimit?.value !== undefined &&
    licenseFeatureThirdPartyAppTotal !== undefined &&
    licenseFeatureThirdPartyAppTotal >= licenseFeatureThirdPartyAppLimit?.value
  );
});
</script>

<template>
  <page-component :name="BREADCRUMBS.APPLICATION_MGT.title" check-license>
    <template v-if="!newInterfaceDisabled" #top-right>
      <lxc-button
        :icon="ILxcPlus"
        :title="t('applicationMgt.newInterface')"
        class="self-end"
        html-type="button"
        type="primary"
        :disabled="isThirdPartyAppLimitReached"
        @click="onNewInterfaceButtonClick"
      >
        {{ t("applicationMgt.newInterface") }}
      </lxc-button>
    </template>
    <third-party-application-list
      v-model:create="createThirdPartyApplication"
      :disable-activate-interface-button="isThirdPartyAppLimitReached"
      @change="fetchActiveLicenseInformation(true)"
    />
  </page-component>
</template>
